import { StandardCheckbox } from '@brandfolder/react';
import { t } from '@lingui/macro';
import classnames from 'classnames';
import React, {
  FunctionComponent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import { statusLabel } from './apiKeysTableHelpers';
import { FilterIcon } from './FilterIcon';
import classes from './styles/api-keys-table.module.scss';
import { ApiKeyStatus } from './types';

interface StatusFilterHeaderProps {
  children: ReactNode;
  onChange: (statuses: ApiKeyStatus[]) => void;
  selected: ApiKeyStatus[];
}

const FILTERABLE_STATUSES: ApiKeyStatus[] = ['active', 'expiring', 'disabled'];

export const StatusFilterHeader: FunctionComponent<StatusFilterHeaderProps> = ({
  children,
  onChange,
  selected,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeydown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeydown);

    return (): void => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [isOpen]);

  const toggleStatus = (status: ApiKeyStatus): void => {
    if (selected.includes(status)) {
      onChange(selected.filter((current) => current !== status));
    } else {
      onChange([...selected, status]);
    }
  };

  return (
    <th className={classnames(classes.headerCell, classes.sortableHeaderCell)} scope="col">
      <div ref={containerRef} className={classes.filterContainer}>
        <button
          aria-expanded={isOpen}
          aria-haspopup="true"
          className={classes.sortButton}
          onClick={(): void => setIsOpen((previous) => !previous)}
          type="button"
        >
          <span>{children}</span>
          <FilterIcon active={selected.length > 0} />
        </button>

        {isOpen && (
          <div className={classes.filterMenu} role="group">
            <p className={classes.filterMenuTitle}>{t`Filter by status`}</p>
            {FILTERABLE_STATUSES.map((status) => (
              <StandardCheckbox
                key={status}
                id={`api-keys-status-filter-${status}`}
                inputProps={{ checked: selected.includes(status) }}
                label={statusLabel(status)}
                onChange={(): void => toggleStatus(status)}
              />
            ))}
          </div>
        )}
      </div>
    </th>
  );
};
